import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Menu from '@mui/material/Menu'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import CloseIcon from '@mui/icons-material/Close'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import AppsIcon from '@mui/icons-material/Apps'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { typeDef, latestVersion, TONE_COLOR } from '../pages/artifactsModel'
import { SERIF_HEADING } from '../theme'
import type { SessionArtifact, PanelArtifactVersion } from './rosArtifacts'

interface ArtifactsPanelProps {
  artifacts: SessionArtifact[]
  onSelectArtifact: (artifact: SessionArtifact) => void
  onDownload: (a: SessionArtifact, v: PanelArtifactVersion) => void
  onDelete: (a: SessionArtifact) => void
  onClose: () => void
}

function RowMenu({ a, onDelete }: { a: SessionArtifact; onDelete: (a: SessionArtifact) => void }) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  return (
    <>
      <Tooltip title="More">
        <IconButton size="small" onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget) }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        <Tooltip title="Coming soon" placement="right">
          <span><MenuItem disabled><EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} />Rename</MenuItem></span>
        </Tooltip>
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); setConfirmOpen(true) }} sx={{ color: 'error.main' }}>
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />Delete artifact…
        </MenuItem>
      </Menu>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete “{a.title}”?</DialogTitle>
        <DialogContent>
          <DialogContentText>This deletes the artifact and all its versions. This can't be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={() => { setConfirmOpen(false); onDelete(a) }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export function ArtifactsPanel({ artifacts, onSelectArtifact, onDownload, onDelete, onClose }: ArtifactsPanelProps) {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.06em', color: 'text.secondary', textTransform: 'uppercase' }}>
            Chat Panels
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
            <AppsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography sx={{ ...SERIF_HEADING, fontSize: '1.05rem' }}>Artifacts</Typography>
            <Box sx={{
              minWidth: 22, height: 22, px: 0.5, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: `${TONE_COLOR.cyan}1F`, color: TONE_COLOR.cyan, fontSize: '0.72rem', fontWeight: 700,
            }}>
              {artifacts.length}
            </Box>
          </Box>
        </Box>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {artifacts.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>No artifacts in this chat yet.</Typography>
          </Box>
        )}
        {artifacts.map(a => {
          const def = typeDef(a.type)
          const lv = latestVersion(a)
          return (
            <Box
              key={a.id}
              onClick={() => onSelectArtifact(a)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, cursor: 'pointer',
                borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <def.icon sx={{ fontSize: 20, color: TONE_COLOR[def.tone], flexShrink: 0 }} />
              <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 600, flex: 1, minWidth: 0 }}>{a.title}</Typography>
              <Typography noWrap sx={{ fontSize: '0.8rem', color: 'text.secondary', flexShrink: 0 }}>
                {def.ext}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.25 }}>
                {a.type !== 'markdown' && (
                  <Tooltip title={`Download ${def.ext}`}>
                    <IconButton size="small" onClick={e => { e.stopPropagation(); onDownload(a, lv) }}>
                      <DownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <RowMenu a={a} onDelete={onDelete} />
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
